import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { CheckCircle2, XCircle, Loader2, Building2, UserCheck, Shield } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const InvitationAcceptance = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [invitation, setInvitation] = useState(null);
  const [companySettings, setCompanySettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Fetch invitation details on mount
  useEffect(() => {
    fetchInvitationDetails();
  }, [token]);

  // Auto-accept invitation after user returns from OAuth login
  useEffect(() => {
    console.log('🔍 Checking if should auto-accept invitation...');
    console.log('   User:', user?.email);
    console.log('   Invitation:', invitation?.email);
    console.log('   Success state:', success);
    console.log('   Accepting state:', accepting);
    
    // Prevent running if already processed or in progress
    if (success || accepting || error) {
      console.log('⏸️ Skipping auto-accept (already processed or in progress)');
      return;
    }
    
    // If user just logged in and invitation is loaded, auto-accept
    if (user && invitation) {
      console.log('✅ Conditions met, checking email match...');
      
      // Check if emails match
      if (user.email === invitation.email) {
        console.log('✅ Email matches, proceeding with acceptance');
        handleAcceptInvitation();
      } else {
        console.log('⚠️ Email mismatch');
        console.log('   User email:', user.email);
        console.log('   Invitation email:', invitation.email);
        setError(`This invitation is for ${invitation.email}, but you're signed in as ${user.email}. Please sign in with the correct account.`);
      }
    }
  }, [user, invitation]); // Removed success, accepting, error from dependencies to prevent re-runs

  const fetchInvitationDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(
        `${BACKEND_URL}/api/v1/users/invitations/${token}`
      );

      setInvitation(response.data);
      console.log('✅ Invitation details loaded:', response.data);
      
      // Fetch company settings for logo
      await fetchCompanySettings();
    } catch (err) {
      console.error('❌ Error fetching invitation:', err);
      
      if (err.response?.status === 404) {
        setError('Invitation not found. It may have been cancelled or already used.');
      } else if (err.response?.status === 410) {
        setError('This invitation has expired. Please contact the team owner for a new invitation.');
      } else {
        setError(err.response?.data?.detail || 'Failed to load invitation details. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanySettings = async () => {
    try {
      // Fetch company settings (public endpoint, no auth required for invitation page)
      const response = await axios.get(
        `${BACKEND_URL}/api/v1/company/settings`
      );

      if (response.data.success) {
        setCompanySettings(response.data.settings);
        console.log('✅ Company settings loaded:', response.data.settings);
      }
    } catch (err) {
      console.log('ℹ️ Could not fetch company settings (may require auth)');
      // Don't fail the invitation page if settings can't be fetched
    }
  };

  const handleAcceptInvitation = async () => {
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('🎫 ACCEPTING INVITATION');
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('Token:', token);
    console.log('User:', user);
    console.log('Invitation:', invitation);
    
    // Check if user is logged in
    if (!user) {
      console.log('❌ User not logged in, redirecting to login');
      toast.error('Please sign in first to accept this invitation');
      // Store the current path to redirect back after login
      sessionStorage.setItem('redirect_after_login', window.location.pathname);
      navigate('/login');
      return;
    }

    try {
      setAccepting(true);
      console.log('📤 Sending POST request to accept invitation...');
      console.log('   URL:', `${BACKEND_URL}/api/v1/users/invitations/${token}/accept`);
      
      const response = await axios.post(
        `${BACKEND_URL}/api/v1/users/invitations/${token}/accept`,
        {},
        { withCredentials: true }
      );

      console.log('✅ Response received');
      console.log('   Status:', response.status);
      console.log('   Data:', response.data);
      
      setSuccess(true);
      toast.success(response.data.message || 'Invitation accepted successfully!');

      // Clear the invitation token from URL and redirect
      console.log('⏱️ Redirecting to dashboard...');
      
      // Use replace to avoid back button issues
      window.history.replaceState({}, '', '/');
      
      // Navigate without reload to prevent loop
      setTimeout(() => {
        console.log('🔄 Navigating now...');
        navigate('/', { replace: true });
      }, 1500);

    } catch (err) {
      console.error('❌ Error accepting invitation');
      console.error('   Error:', err);
      console.error('   Response:', err.response);
      console.error('   Status:', err.response?.status);
      console.error('   Data:', err.response?.data);
      
      if (err.response?.status === 401) {
        console.log('❌ 401 Unauthorized - redirecting to login');
        toast.error('Please sign in first to accept this invitation');
        sessionStorage.setItem('redirect_after_login', window.location.pathname);
        navigate('/login');
      } else if (err.response?.status === 403) {
        console.log('❌ 403 Forbidden - email mismatch or access denied');
        setError('This invitation is for a different email address. Please sign in with the correct account.');
      } else if (err.response?.status === 410) {
        console.log('❌ 410 Gone - invitation expired');
        setError('This invitation has expired. Please contact the team owner for a new invitation.');
      } else {
        console.log('❌ Other error:', err.response?.data?.detail || err.message);
        setError(err.response?.data?.detail || 'Failed to accept invitation. Please try again.');
      }
    } finally {
      setAccepting(false);
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-12 w-12 animate-spin text-blue-600 mb-4" />
            <p className="text-gray-600 dark:text-gray-400">Loading invitation details...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  if (error && !invitation) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex items-center justify-center mb-4">
              <XCircle className="h-16 w-16 text-red-500" />
            </div>
            <CardTitle className="text-center text-2xl">Invalid Invitation</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-center text-gray-600 dark:text-gray-400">{error}</p>
            <div className="flex justify-center">
              <Button onClick={() => navigate('/login')} className="w-full">
                Go to Sign In
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex items-center justify-center mb-4">
              <CheckCircle2 className="h-16 w-16 text-green-500" />
            </div>
            <CardTitle className="text-center text-2xl">Invitation Accepted!</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-center text-gray-600 dark:text-gray-400">
              You've successfully joined {invitation?.company_name}. Redirecting to dashboard...
            </p>
            <div className="flex justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main invitation acceptance UI
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex items-center justify-center mb-4">
            {companySettings?.logo_url ? (
              <img 
                src={companySettings.logo_url} 
                alt={`${invitation?.company_name} logo`}
                className="h-16 w-16 rounded-lg object-cover"
              />
            ) : (
              <div className="h-16 w-16 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                <Building2 className="h-8 w-8 text-blue-600 dark:text-blue-400" />
              </div>
            )}
          </div>
          <CardTitle className="text-center text-2xl">You're Invited!</CardTitle>
          <CardDescription className="text-center">
            Join your team on {invitation?.company_name || 'ZuperBooks'}
          </CardDescription>
          {invitation?.company_name && (
            <p className="text-center text-xs text-gray-500 dark:text-gray-400 mt-2">
              Powered by ZuperBooks
            </p>
          )}
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Invitation Details */}
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-3">
            <div className="flex items-start gap-3">
              <Building2 className="h-5 w-5 text-gray-500 mt-0.5" />
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Company</p>
                <p className="font-semibold text-gray-900 dark:text-gray-100">
                  {invitation?.company_name}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <UserCheck className="h-5 w-5 text-gray-500 mt-0.5" />
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Invited by</p>
                <p className="font-semibold text-gray-900 dark:text-gray-100">
                  {invitation?.invited_by_name}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Shield className="h-5 w-5 text-gray-500 mt-0.5" />
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Role</p>
                <p className="font-semibold text-gray-900 dark:text-gray-100">
                  {invitation?.role_name}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Shield className="h-5 w-5 text-gray-500 mt-0.5" />
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Email</p>
                <p className="font-semibold text-gray-900 dark:text-gray-100">
                  {invitation?.email}
                </p>
              </div>
            </div>
          </div>

          {/* Error message */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          {/* User status */}
          {!user && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <p className="text-sm text-blue-600 dark:text-blue-400">
                You need to sign in with Google first to accept this invitation.
              </p>
            </div>
          )}

          {user && user.email !== invitation?.email && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
              <p className="text-sm text-amber-600 dark:text-amber-400">
                You're currently signed in as <strong>{user.email}</strong>. This invitation is for{' '}
                <strong>{invitation?.email}</strong>. Please sign in with the correct account.
              </p>
            </div>
          )}

          {/* Action buttons */}
          <div className="space-y-3">
            {!user ? (
              <Button
                onClick={() => {
                  sessionStorage.setItem('redirect_after_login', window.location.pathname);
                  navigate('/login');
                }}
                className="w-full"
              >
                Sign In to Accept
              </Button>
            ) : user.email === invitation?.email ? (
              <Button
                onClick={handleAcceptInvitation}
                disabled={accepting}
                className="w-full"
              >
                {accepting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Accepting...
                  </>
                ) : (
                  'Accept Invitation'
                )}
              </Button>
            ) : (
              <Button
                onClick={() => navigate('/login')}
                className="w-full"
                variant="outline"
              >
                Sign In with Correct Account
              </Button>
            )}

            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="w-full"
            >
              Cancel
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default InvitationAcceptance;
